import React, { useState, useRef, useEffect } from 'react';
import { TextInput, Paper, Badge, Text } from '@mantine/core';
import axiosInstance from './../axiosInstance.js';
import { DIMENSION_COLORS, dimensionLabel } from './ArtTags.js';

// Prefix search over the tag vocabulary; onPick({dimension, value, ru}).
const TagPicker = ({ language = 'ru', exclude = [], onPick }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [highlighted, setHighlighted] = useState(-1);
  const timerRef = useRef(null);
  const seqRef = useRef(0);

  useEffect(() => {
    clearTimeout(timerRef.current);
    const term = q.trim();
    if (term === '') { setResults(null); return; }
    const seq = ++seqRef.current;
    timerRef.current = setTimeout(() => {
      axiosInstance.get(`/tags/search?q=${encodeURIComponent(term)}&limit=15`)
        .then((r) => {
          if (seq !== seqRef.current) return;
          const taken = new Set(exclude.map(t => `${t.dimension}:${t.value}`));
          setResults(r.data.filter(t => !taken.has(`${t.dimension}:${t.value}`)));
          setHighlighted(-1);
        })
        .catch((e) => console.error(e));
    }, 120);
    return () => clearTimeout(timerRef.current);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (t) => {
    setQ('');
    setResults(null);
    onPick(t);
  };

  return (
    <div className="relative" style={{ minWidth: 200 }}>
      <TextInput
        size="xs"
        radius="sm"
        value={q}
        placeholder={language === 'ru' ? '+ добавить тег' : '+ add tag'}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (!results || results.length === 0) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => (h + 1) % results.length); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted(h => (h <= 0 ? results.length - 1 : h - 1)); }
          else if (e.key === 'Enter' && highlighted >= 0) { e.preventDefault(); pick(results[highlighted]); }
          else if (e.key === 'Escape') { setResults(null); }
        }}
      />
      {results && (
        <Paper shadow="md" radius="md" withBorder className="absolute left-0 z-20 mt-1 w-full" style={{ minWidth: 260, maxHeight: 320, overflowY: 'auto' }}>
          {results.length === 0 ? (
            <Text size="xs" c="dimmed" p="xs">{language === 'ru' ? 'Нет таких тегов' : 'No such tags'}</Text>
          ) : results.map((t, i) => (
            <div
              key={`${t.dimension}:${t.value}`}
              className={`flex items-center gap-2 px-2 py-1 cursor-pointer ${i === highlighted ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); pick(t); }}
              onMouseEnter={() => setHighlighted(i)}
            >
              <Badge color={DIMENSION_COLORS[t.dimension] || 'gray'} variant="light" size="sm" radius="sm" style={{ textTransform: 'none' }}>
                {language === 'ru' && t.ru ? t.ru : t.value}
              </Badge>
              <Text size="xs" c="dimmed">{dimensionLabel(t.dimension, language)}</Text>
              {t.count > 0 && <Text size="xs" c="dimmed" ml="auto">{t.count}</Text>}
            </div>
          ))}
        </Paper>
      )}
    </div>
  );
};

export default TagPicker;
